import * as React from 'react';

import type {
  IdentityFormErrors,
  IdentityFormState,
} from '../use-edit-identity-panel/useEditIdentityPanel.types';
import type { UseBirthdateFieldReturn } from './useBirthdateField.types';

import { parseBirthdateInput } from '../use-edit-identity-panel/useEditIdentityPanel.utils';

function useBirthdateField(
  setFormState: React.Dispatch<React.SetStateAction<IdentityFormState>>,
  setErrors: React.Dispatch<React.SetStateAction<IdentityFormErrors>>
): UseBirthdateFieldReturn {
  const birthdateRawInputRef = React.useRef<string>('');
  const isDateSelectFromTypingRef = React.useRef<boolean>(false);
  const [birthdateInputDirty, setBirthdateInputDirty] =
    React.useState<boolean>(false);

  const handleDateInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const raw = e.target.value;
      birthdateRawInputRef.current = raw;
      isDateSelectFromTypingRef.current = true;
      setErrors(prev => ({ ...prev, birthdate: undefined }));

      if (raw === '') {
        setFormState(prev => ({ ...prev, birthdate: null }));
        setBirthdateInputDirty(false);
        return;
      }

      const parsed = parseBirthdateInput(raw);
      if (parsed !== null) {
        setFormState(prev => ({ ...prev, birthdate: parsed }));
        setBirthdateInputDirty(false);
      } else {
        setBirthdateInputDirty(true);
      }
    },
    [setFormState, setErrors]
  );

  const handleDateSelect = React.useCallback(
    (date: Date | [Date, Date] | null): void => {
      const fromTyping = isDateSelectFromTypingRef.current;
      isDateSelectFromTypingRef.current = false;

      if (fromTyping) {
        if (date instanceof Date) {
          setFormState(prev => ({ ...prev, birthdate: date }));
        }
        return;
      }

      birthdateRawInputRef.current = '';
      setBirthdateInputDirty(false);
      setErrors(prev => ({ ...prev, birthdate: undefined }));
      setFormState(prev => ({
        ...prev,
        birthdate: date instanceof Date ? date : null,
      }));
    },
    [setFormState, setErrors]
  );

  return {
    birthdateRawInputRef,
    birthdateInputDirty,
    handleDateInputChange,
    handleDateSelect,
  };
}

export { useBirthdateField };
